import { EXIT_LIVE_PAN_PX } from "./camera_gesture"

// The self-follow camera state machine — the web port of the app's tested module
// (react-native/src/map/cameraMode.js). Every "Live button did the wrong thing"
// bug in this app's history was an illegal transition between hand-set booleans;
// here the legal moves are one switch statement, and both platforms step the
// SAME table.
//
// Modes (one value replaces the old liveMode/following/resume-timer triple):
//   explore    browsing; nothing follows you
//   liveIdle   Live context but not following (post-trip, or orientation "off")
//   following  camera locked to you
//   paused     following, but a gesture suspended it; auto-recenter may be armed
//
// Events return { mode, effects } — the controller executes effects, the reducer
// never touches timers/camera itself:
//   armRecenter    start the auto-recenter countdown (paused → following later)
//   clearRecenter  cancel that countdown
//   cancelEngage   cancel any pending fly-to-user retries (no-op on web today)
// Engagement itself (fly to the user) is NOT an effect: the controller flies
// whenever the mode transitions INTO "following", so there is exactly one
// engage path no matter which event caused it.

export const CAMERA_MODES = ["explore", "liveIdle", "following", "paused"]

export function cameraModeReducer(mode, event) {
  const stay = (effects = []) => ({ mode, effects })
  const to = (next, effects = []) => ({ mode: next, effects })

  switch (event.type) {
    // The button displays the current view. Every Live state exits to Explore;
    // Explore enters Live follow and recenters on the user.
    case "TAP_LIVE":
      return mode === "explore"
        ? to("following", ["clearRecenter"])
        : to("explore", ["cancelEngage", "clearRecenter"])

    case "RECENTER_SELF": // ◎ / follow button — caller performs its own fly
      return to("following", ["clearRecenter"])

    // Starting a trip is an explicit request to enter Live follow. The follow
    // transition owns the one-time move to the user's current location.
    case "TRACKING_STARTED":
      return to("following", ["clearRecenter"])

    case "TRACKING_STOPPED": // stay in the Live context, but stop following
      return mode === "explore" ? stay(["clearRecenter"]) : to("liveIdle", ["clearRecenter"])

    // A user gesture began: pause follow so the camera can't fight the input.
    case "GESTURE_START":
      return mode === "following" || mode === "paused" ? to("paused", ["cancelEngage", "armRecenter"]) : stay(["cancelEngage"])

    // Zoom/tilt in place while following → the caller saved the new zoom/tilt;
    // resume following immediately at the adjusted camera.
    case "GESTURE_ADJUSTED":
      return mode === "paused" ? to("following", ["clearRecenter"]) : stay()

    // The puck left the viewport — the user panned themselves off screen, so
    // Live has nothing left to follow. This is the ONLY pan-driven exit, and it
    // fires the moment it becomes true rather than waiting for the gesture to
    // end. Kept in lockstep with the native reducer (test/fixtures/
    // camera_mode_sequences.json is the shared contract).
    case "PUCK_OFF_SCREEN":
      return mode === "explore"
        ? stay()
        : to("explore", ["cancelEngage", "clearRecenter"])

    // Pan finished with the puck still on screen: you are looking around inside
    // Live, so stay paused and let auto-recenter bring the camera back. Pixels of
    // pan never answered "can I still see myself?", so the distance rule now
    // survives only as the no-fix fallback.
    case "GESTURE_PANNED":
      if (mode !== "paused") return stay()
      return event.exitWithoutPuck && event.px > EXIT_LIVE_PAN_PX
        ? to("explore", ["clearRecenter"])
        : stay()

    case "AUTO_RECENTER": // the paused countdown fired → resume following
      return mode === "paused" ? to("following") : stay()

    case "SELF_ENGAGE_MISSED":
      return mode === "following" ? to("liveIdle", ["clearRecenter"]) : stay()

    case "ORIENT_OFF": // orientation ring reached "off" / follow toggled off
      return to("liveIdle", ["clearRecenter"])

    case "ORIENT_ENGAGE": // orientation ring picked a mode → make sure we follow
      return to("following", ["clearRecenter"])

    // Search fly-to, find-friend, locate failure: something else owns the camera.
    case "EXTERNAL_RELEASE":
      return to("explore", ["cancelEngage", "clearRecenter"])

    default:
      return stay()
  }
}
